import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { CTAButton } from "../components/CTAButton";
import { StatsBand, EditorialMarquee, Pillars, ClosingCTA } from "../components/Sections";
import { PM_SERVICES, IMAGES } from "../data";

export default function PropertyManagement() {
  const [active, setActive] = useState(0);
  const service = PM_SERVICES[active];

  return (
    <div data-testid="property-management-page">
      <PageHero
        label="Property Management"
        title="Everything your property needs, managed by one team."
        intro="You bought the property to build wealth, not to manage it. Kept operates as an extension of your team, protecting asset value, avoiding compliance penalties, and keeping ownership genuinely hands-off. Explore each part of our service below."
        image={IMAGES.whiteHouse}
      />

      {/* Strong CTA push */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-8" data-testid="pm-cta-primary">
        <Reveal>
          <div className="bg-kept-ink grain p-10 md:p-16 flex flex-col md:flex-row md:items-center justify-between gap-8">
            <div className="max-w-xl">
              <div className="kept-label text-kept-brass mb-4">Start here</div>
              <h2 className="font-serif text-3xl md:text-4xl font-light text-kept-sand leading-tight">
                Find out what your property should be earning.
              </h2>
              <p className="text-kept-sand/60 mt-4">A complimentary, no-obligation rental appraisal. We respond within one business day.</p>
            </div>
            <CTAButton light testId="pm-book-appraisal" />
          </div>
        </Reveal>
      </section>

      {/* Sub-tab explorer */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="pm-services-section">
        <div className="max-w-2xl mb-16">
          <div className="kept-label brass-text mb-6">Our services</div>
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-normal tracking-tight text-kept-ink leading-tight">
              Complete management, one accountable partner.
            </h2>
          </Reveal>
        </div>

        <div className="grid lg:grid-cols-12 gap-px bg-kept-ink/10 border border-kept-ink/10">
          {/* Tab rail */}
          <div className="lg:col-span-5 bg-kept-sand" role="tablist" aria-label="Property management services" data-testid="pm-tab-rail">
            {PM_SERVICES.map((s, i) => (
              <button
                key={s.n}
                role="tab"
                aria-selected={active === i}
                onClick={() => setActive(i)}
                className={`group w-full text-left flex items-center gap-6 px-8 py-6 border-b border-kept-ink/10 last:border-b-0 transition-colors duration-400 ${
                  active === i ? "bg-kept-ink" : "hover:bg-kept-stone/60"
                }`}
                data-testid={`pm-tab-${s.n}`}
              >
                <span className={`font-serif text-xl transition-colors duration-400 ${active === i ? "text-kept-brass" : "brass-text"}`}>{s.n}</span>
                <span className={`font-serif text-lg md:text-xl leading-snug transition-colors duration-400 ${active === i ? "text-kept-sand" : "text-kept-ink"}`}>
                  {s.title}
                </span>
              </button>
            ))}
          </div>

          {/* Panel */}
          <div className="lg:col-span-7 bg-kept-stone p-10 md:p-16 flex flex-col" data-testid="pm-panel">
            <AnimatePresence mode="wait">
              <motion.div
                key={service.n}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              >
                <div className="font-serif text-6xl font-light brass-text">{service.n}</div>
                <h3 className="font-serif text-2xl md:text-3xl text-kept-ink mt-6 leading-tight">{service.title}</h3>
                <p className="text-muted-foreground mt-6 text-base md:text-lg leading-relaxed">{service.body}</p>
                <div className="mt-10">
                  <CTAButton testId={`pm-panel-cta-${service.n}`} />
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>

      <StatsBand />
      <EditorialMarquee />
      <Pillars label="Why owners choose Kept" heading="Built for owners who demand excellence." />
      <ClosingCTA
        heading="Get your free rental appraisal."
        sub="Let us handle appraisal, tenant placement, compliance, and financial reporting, so you can focus on what matters."
      />
    </div>
  );
}
